import { Component, computed, input, output, ViewEncapsulation } from '@angular/core';
import { type ModalVariant, type ModalBadgeColor } from './modal.model';
import { ButtonComponent } from '../button';
import { BadgeComponent } from '../badge';

@Component({
  selector: 'lib-modal',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [ButtonComponent, BadgeComponent],
  host: {
    '(click)': 'onBackdropClick($event)',
    '(keydown.escape)': 'closed.emit()',
    'style': 'position:fixed;inset:0;z-index:50;display:block;',
  },
  template: `
    <!-- Backdrop -->
    <div class="msa-modal__backdrop">
      <!-- Panel -->
      <div class="msa-modal__panel" [class]="panelClass()" [style]="panelStyle()" (click)="$event.stopPropagation()">

        <!-- ═══ Detail header ═══ -->
        @if (variant() === 'detail') {
        <div class="msa-modal__header">
          <div class="msa-modal__header-left">
            @if (avatarInitials()) {
            <div class="msa-modal__avatar">{{ avatarInitials() }}</div>
            }
            <div>
              <h3 class="msa-modal__title">{{ title() }}</h3>
              @if (subtitle()) {
              <p class="msa-modal__subtitle">{{ subtitle() }}</p>
              }
            </div>
          </div>
          <div class="msa-modal__header-right">
            @if (badgeLabel()) {
            <lib-badge [label]="$any(badgeLabel())" [color]="badgeColor()" />
            }
            <lib-button
              [iconSvg]="closeIconSvg"
              variant="ghost"
              color="neutral"
              (clicked)="closed.emit()"
            />
          </div>
        </div>
        }

        <!-- ═══ Form header ═══ -->
        @if (variant() === 'form') {
        <div class="msa-modal__header msa-modal__header--form">
          <div>
            <h2 class="msa-modal__title">{{ title() }}</h2>
            @if (subtitle()) {
            <p class="msa-modal__subtitle">{{ subtitle() }}</p>
            }
          </div>
          <lib-button
            [iconSvg]="closeIconSvg"
            variant="ghost"
            color="neutral"
            (clicked)="closed.emit()"
          />
        </div>
        }

        <!-- Body (content projection) -->
        <div class="msa-modal__body">
          <ng-content select="[modal-body]" />
        </div>

        <!-- Footer (content projection) -->
        @if (hasFooter()) {
        <div class="msa-modal__footer">
          <ng-content select="[modal-footer]" />
        </div>
        }

        <!-- Full-panel loading overlay -->
        @if (loading()) {
        <div class="msa-modal__loading-overlay">
          <div class="msa-modal__ball-wrapper">
            <div class="msa-modal__ball-shadow"></div>
            <div class="msa-modal__ball">⚽</div>
          </div>
          @if (loadingText()) {
          <span class="msa-modal__loading-text">{{ loadingText() }}</span>
          }
        </div>
        }

      </div>
    </div>
  `,
  styles: [`
    /* ═══════════════════════════════════════════════
       MSA Modal Compound
       ═══════════════════════════════════════════════
       Two variants: detail (avatar+badge) and form (gray header).
       Content projection: [modal-body], [modal-footer].
       ViewEncapsulation.None — host styles set via binding.
       ═══════════════════════════════════════════════ */

    /* ──────────────────────────────────────
       Backdrop
       ────────────────────────────────────── */
    .msa-modal__backdrop {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(0, 0, 0, 0.4);
      backdrop-filter: blur(4px);
      padding: 16px;
    }

    /* ──────────────────────────────────────
       Panel
       ────────────────────────────────────── */
    .msa-modal__panel {
      background: #fff;
      border-radius: 16px;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
      width: 100%;
      max-width: 512px;
      max-height: 90vh;
      overflow-y: auto;
      font-family: inherit;
      position: relative;
      z-index: 1;
    }

    .msa-modal__panel--form {
      max-width: 560px;
      border-radius: 12px;
      border: 1px solid #e5e7eb;
    }

    /* ──────────────────────────────────────
       Header
       ────────────────────────────────────── */
    .msa-modal__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 20px;
      border-bottom: 1px solid #f1f5f9;
    }

    .msa-modal__header--form {
      background: #f9fafb;
      border-bottom: 1px solid #e5e7eb;
      align-items: flex-start;
    }

    .msa-modal__header-left {
      display: flex;
      align-items: center;
      gap: 12px;
      min-width: 0;
    }

    .msa-modal__header-right {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-shrink: 0;
    }

    /* ──────────────────────────────────────
       Avatar
       ────────────────────────────────────── */
    .msa-modal__avatar {
      width: 40px;
      height: 40px;
      border-radius: 12px;
      background: #dbeafe;
      color: #2563eb;
      font-weight: 700;
      font-size: 13px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    /* ──────────────────────────────────────
       Typography
       ────────────────────────────────────── */
    .msa-modal__title {
      margin: 0;
      font-size: 15px;
      font-weight: 700;
      color: #0b1c33;
      line-height: 1.3;
    }

    .msa-modal__header--form .msa-modal__title {
      font-size: 16px;
    }

    .msa-modal__subtitle {
      margin: 2px 0 0;
      font-size: 12px;
      color: #6b7280;
    }

    /* ──────────────────────────────────────
       Body
       ────────────────────────────────────── */
    .msa-modal__body {
      padding: 20px;
    }

    /* ──────────────────────────────────────
       Footer
       ────────────────────────────────────── */
    .msa-modal__footer {
      padding: 20px;
      border-top: 1px solid #f1f5f9;
      display: flex;
      flex-wrap: nowrap;
      align-items: center;
      justify-content: flex-end;
      gap: 10px;
    }

    /* Consumer's <div modal-footer> wrapper inside the slot */
    .msa-modal__footer [modal-footer] {
      display: contents;
    }

    .msa-modal__footer > * {
      flex-shrink: 0;
    }

    .msa-modal--form .msa-modal__footer {
      border-top: 1px solid #f1f5f9;
    }

    /* ──────────────────────────────────────
       Loading overlay (covers the whole panel)
       ────────────────────────────────────── */
    .msa-modal__loading-overlay {
      position: absolute;
      inset: 0;
      z-index: 10;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 10px;
      background: rgba(255, 255, 255, 0.85);
      border-radius: inherit;
    }

    .msa-modal__loading-text {
      font-size: 12.5px;
      font-weight: 600;
      color: #4b5563;
    }

    .msa-modal__ball-wrapper {
      position: relative;
      width: 60px;
      height: 68px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-end;
    }

    .msa-modal__ball-shadow {
      width: 38px;
      height: 8px;
      background: rgba(0, 0, 0, 0.2);
      border-radius: 50%;
      animation: msa-modal-shadow-pulse 0.8s ease-in-out infinite;
      position: absolute;
      bottom: 4px;
    }

    .msa-modal__ball {
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 32px;
      line-height: 1;
      filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.25));
      animation: msa-modal-bounce 0.8s ease-in-out infinite;
      transform-origin: center bottom;
      position: absolute;
      bottom: 12px;
    }

    @keyframes msa-modal-bounce {
      0%, 100% {
        transform: translateY(0);
        animation-timing-function: ease-out;
      }
      50% {
        transform: translateY(-30px);
        animation-timing-function: ease-in;
      }
    }

    @keyframes msa-modal-shadow-pulse {
      0%, 100% {
        transform: scale(1);
        opacity: 0.2;
        animation-timing-function: ease-out;
      }
      50% {
        transform: scale(0.55);
        opacity: 0.08;
        animation-timing-function: ease-in;
      }
    }
  `],
})
export class ModalComponent {
  /** Modal variant: 'detail' (avatar+badge) or 'form' (gray header). */
  readonly variant = input<ModalVariant>('detail');

  /** Header title. */
  readonly title = input.required<string>();

  /** Header subtitle/description. */
  readonly subtitle = input<string>();

  /** Avatar initials (detail variant only). Shows square with initials. */
  readonly avatarInitials = input<string>();

  /** Badge label in header (detail variant only). */
  readonly badgeLabel = input<string>();

  /** Badge color. */
  readonly badgeColor = input<ModalBadgeColor>('blue');

  /** Show footer section. */
  readonly hasFooter = input<boolean>(true);

  /** Shows a full-panel loading overlay on top of the modal content. */
  readonly loading = input<boolean>(false);

  /** Optional text shown below the spinner in the loading overlay. */
  readonly loadingText = input<string>();

  /** Emitted when backdrop clicked or close button pressed. */
  readonly closed = output<void>();

  /** Panel max-width (CSS value). Overrides default variant max-width. */
  readonly maxWidth = input<string>();

  /** Panel max-width class. */
  readonly panelClass = computed(() => {
    const v = this.variant();
    const classes = v === 'form' ? 'msa-modal__panel--form' : '';
    return classes;
  });

  /** Panel inline style with max-width override. */
  readonly panelStyle = computed(() => {
    const mw = this.maxWidth();
    return mw ? { 'max-width': mw } : {};
  });

  /** Close icon SVG (X). */
  readonly closeIconSvg = '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>';

  /** Close on backdrop click. */
  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('msa-modal__backdrop')) {
      this.closed.emit();
    }
  }
}
